import { atom, useAtom, useSetAtom } from "jotai";
import { PropsWithChildren, useEffect, useRef } from "react";
import { newToastEvent } from "../utils/ToastUtil";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import useWorkspace from "../api/api-hooks/workspaces/useWorkspace";
import useWorkspaces from "../api/api-hooks/workspaces/useWorkspaces";
import { pageSettingsAtom } from "../atoms/PageAtoms";
import { workspaceIdAtom } from "../atoms/WorkspaceAtoms";
import PageSettingsDrawer from "../components/Drawers/PageSettingsDrawer";

// Atom that stores whether or not we are still processing/calculating the current workspace.
export const WorkspaceContextLoadingAtom = atom(true);

const WorkspaceContext = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantID } = useParams();
  const { workspace, workspaceLoading } = useWorkspace();
  const {
    defaultWorkspace,
    userOwnedWorkspaces,
    userCollabWorkspaces,
    workspacesLoading,
    workspaceIds,
  } = useWorkspaces();
  const [selectedPage, setSelectedPage] = useAtom(pageSettingsAtom);
  const [wsAtomID, setWorkspaceID] = useAtom(workspaceIdAtom);
  const setContextLoading = useSetAtom(WorkspaceContextLoadingAtom);
  const workspaceID = getURLWorkspaceID();
  const dWorkspaceIdRef = useRef();
  dWorkspaceIdRef.current = defaultWorkspace?.id;

  //------------------------- URL Matching useEffect ---------------------
  // ------ Handles workspaceID setup, ensures workspaceID is valid ------
  useEffect(() => {
    if (workspacesLoading) return;

    // Handle no workspace in URL. For pages like "brie" and "view all workspaces".
    // (On page reload the workspace would be lost otherwise)
    if (!workspaceID) {
      let lastWorkspace = localStorage.getItem("workspaceID");

      if (lastWorkspace && workspaceIds.includes(lastWorkspace)) {
        updateWorkspaceID(lastWorkspace);
        return;
      }

      if (dWorkspaceIdRef.current) {
        updateWorkspaceID(dWorkspaceIdRef.current);
        return;
      }

      if (userOwnedWorkspaces?.[0]?.id) {
        updateWorkspaceID(userOwnedWorkspaces[0].id);
        return;
      }

      if (userCollabWorkspaces?.[0]?.id) {
        updateWorkspaceID(userCollabWorkspaces[0].id);
        return;
      }

      removeWorkspaceID();
      return;
    }

    // Handle workspaceIds doesn't include workspaceID
    if (!workspaceIds.includes(workspaceID)) {
      newToastEvent("error", "Page does not exist.");
      removeWorkspaceID();
      return;
    }

    // Handle sync workspaceID with atom
    if (wsAtomID !== workspaceID) {
      updateWorkspaceID(workspaceID);
      return;
    }
  }, [
    workspace,
    userOwnedWorkspaces,
    userCollabWorkspaces,
    workspacesLoading,
    setWorkspaceID,
    navigate,
    tenantID,
    workspaceID,
    workspaceLoading,
  ]);

  return (
    <>
      {children}
      <PageSettingsDrawer
        open={selectedPage !== null}
        onClose={() => setSelectedPage(null)}
        page={selectedPage}
      />
    </>
  );

  function getURLWorkspaceID() {
    if (!location.pathname) return null;
    const pattern = /workspaces\/([^\/]+)/;
    const idMatch = location.pathname.match(pattern);

    if (idMatch) return idMatch[1];

    return null;
  }

  function updateWorkspaceID(newID: string) {
    setContextLoading(false);
    setWorkspaceID(newID);
    localStorage.setItem("workspaceID", newID);
  }

  function removeWorkspaceID() {
    setContextLoading(false);
    setWorkspaceID(null);
    localStorage.removeItem("workspaceID");
    navigate(`/clouds/${tenantID}/workspaces`);
  }
};

export default WorkspaceContext;
